import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function AILeadCapturePage() {
  const features = [
    'AI-Powered Chatbots',
    '24/7 Lead Capture',
    'Intelligent Qualification',
    'Instant Response System',
    'Multi-Language Support',
    'Appointment Scheduling',
    'SMS & Email Integration',
    'CRM Auto-Sync',
    'Conversation Analytics',
    'Custom Training',
    'HIPAA-Compliant',
    'Human Handoff Protocol'
  ];

  const capabilities = [
    {
      icon: 'ri-chat-4-line',
      title: 'Instant Patient Engagement',
      description: 'AI responds to website visitors instantly, answering questions and capturing lead information 24/7.'
    },
    {
      icon: 'ri-question-answer-line',
      title: 'Answer Common Questions',
      description: 'Trained on your services, pricing, and procedures to answer patient questions accurately and naturally.'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Book Appointments',
      description: 'AI can check availability and schedule appointments directly, reducing friction in the booking process.'
    },
    {
      icon: 'ri-user-search-line',
      title: 'Qualify Leads',
      description: 'Ask qualifying questions to identify high-intent patients and prioritize follow-up for your team.'
    },
    {
      icon: 'ri-notification-3-line',
      title: 'Instant Notifications',
      description: 'Get real-time alerts when high-priority leads engage, so you can follow up immediately.'
    },
    {
      icon: 'ri-team-line',
      title: 'Seamless Handoff',
      description: 'Transfer conversations to your team when needed, with full context and conversation history.'
    }
  ];

  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Never Miss a Lead',
      description: 'Capture leads 24/7, even when your office is closed or your team is busy with patients.'
    },
    {
      icon: 'ri-speed-line',
      title: 'Instant Response',
      description: 'Respond to inquiries in seconds, not hours, dramatically improving conversion rates.'
    },
    {
      icon: 'ri-user-add-line',
      title: 'Increase Conversions',
      description: 'Convert more website visitors into leads and appointments with immediate, helpful engagement.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Lower Cost Per Lead',
      description: 'Automate lead capture and qualification, reducing the cost and time required per patient acquisition.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Improve Patient Experience',
      description: 'Provide instant answers and support, creating a better first impression and patient experience.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'HIPAA-Compliant',
      description: 'All AI interactions follow healthcare privacy guidelines and secure data handling protocols.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Training',
      description: 'We learn about your practice, services, and common patient questions to train the AI properly.'
    },
    {
      step: '02',
      title: 'AI Configuration',
      description: 'Set up and customize the AI chatbot with your branding, tone, and conversation flows.'
    },
    {
      step: '03',
      title: 'Integration',
      description: 'Integrate with your website, CRM, booking system, and communication tools.'
    },
    {
      step: '04',
      title: 'Testing & Refinement',
      description: 'Test conversations, refine responses, and ensure the AI handles all scenarios properly.'
    },
    {
      step: '05',
      title: 'Launch & Monitor',
      description: 'Deploy the AI and monitor conversations to ensure quality and identify improvement opportunities.'
    },
    {
      step: '06',
      title: 'Ongoing Optimization',
      description: 'Continuously improve AI responses based on real conversations and patient feedback.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                AI-POWERED LEAD CAPTURE
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Capture &amp; Convert Leads 24/7 with AI Patient Engagement
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                HIPAA-compliant AI chatbots that engage website visitors instantly, answer questions, qualify leads, and book appointments around the clock.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/book-strategy-call"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap font-bold"
                >
                  <span>Get Your AI Demo</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="#process"
                  className="inline-flex items-center gap-2 bg-white text-[#7C3AED] border-2 border-[#7C3AED] px-8 py-4 rounded-lg hover:bg-[#7C3AED]/5 transition-all cursor-pointer whitespace-nowrap font-medium"
                >
                  <span>See How It Works</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/0bdae1dab2860cf710c59ed6167faea0.jpg"
                  alt="AI lead capture"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              AI CAPABILITIES
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              What Our AI Can Do For Your Practice
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Intelligent automation that handles patient engagement, qualification, and booking without human intervention.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${capability.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {capability.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              WHY AI LEAD CAPTURE
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Turn Your Website Into a 24/7 Patient Acquisition Machine
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              AI-powered engagement captures more leads, converts more visitors, and provides better patient experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=AI%20chatbot%20analytics%20dashboard%20showing%20conversation%20metrics%20lead%20qualification%20appointment%20bookings%20and%20patient%20engagement%20statistics%20for%20healthcare%20practice%20professional%20AI%20management%20interface%20purple%20accents%20clean%20modern%20design&width=600&height=700&seq=aifeatures1&orientation=portrait"
                  alt="AI features dashboard"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                COMPREHENSIVE FEATURES
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Enterprise AI Built for Healthcare
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Advanced AI technology with all the features healthcare practices need for compliant, effective patient engagement.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#7C3AED]/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-[#7C3AED] text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              OUR PROCESS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              From Setup to Optimization
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              We handle everything from AI training to integration to ongoing optimization for maximum results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-xl flex items-center justify-center mb-6">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Ready to Capture More Leads with AI?
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed">
            See a live demo of our AI chatbot and learn how it can transform your patient acquisition.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="/book-strategy-call"
              className="inline-flex items-center gap-2 bg-white text-[#0D9488] px-8 py-4 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap font-bold"
            >
              <span>Get Your AI Demo</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <span>See AI In Action</span>
            </a>
          </div>
        </div>
      </section>

      <RelatedServices currentSlug="ai-lead-capture" />
      <RelatedIndustries />

      <Footer />
    </div>
  );
}
